import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setInsurance } from "../../redux/insuranceSlice";
import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import { Box, Typography, TextField, FormLabel, Grid, MenuItem, Collapse, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import relationships from "../../assets/insure-relations.json";
import { continueButtonSx, inputLabelSx, textFieldSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function Concern() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const theme = useTheme();
    const { returning = false } = location.state || {};
    const insurance = useSelector((state) => state.insurance);

    const [id, setId] = useState(insurance.memberID);
    const [idError, setIdError] = useState(false);
    const [relat, setRelat] = useState(insurance.policyOwner.relationship);
    const [relError, setRelError] = useState(false);

    // not sure if there's a more efficient way to do this
    const [ownerFname, setOwnerFname] = useState(insurance.policyOwner.fname);
    const [ownerFnameError, setOwnerFnameError] = useState(false);
    const [ownerLname, setOwnerLname] = useState(insurance.policyOwner.lname);
    const [ownerLnameError, setOwnerLnameError] = useState(false);
    const [ownerGender, setOwnerGender] = useState(insurance.policyOwner.gender);
    const [ownerGenderError, setOwnerGenderError] = useState(false);
    const [ownerDOB, setOwnerDOB] = useState(insurance.policyOwner.dob);
    const [ownerDOBError, setOwnerDOBError] = useState(false);

    const genderOpts = ["Female", "Male", "Other", "Unknown"];

    const dobErrorMessage = {
        invalidDate: "Please enter a valid date.",
        minDate: "Please enter a valid date.",
        disableFuture: "Please enter a date in the past.",
        required: "This field is required.",
    };

    const isNextEnabled =
        id &&
        relat &&
        (relat === "Self" || (ownerFname && ownerLname && ownerGender && !ownerDOBError));

    const handleNext = () => {
        dispatch(
            setInsurance({
                ...insurance,
                policyOwner: {
                    relationship: relat,
                    fname: ownerFname,
                    lname: ownerLname,
                    gender: ownerGender,
                    dob: ownerDOB,
                },
                memberID: id,
            })
        );

        if (returning) {
            setPageAndDeferNavigate(dispatch, '/anything-else-toknow', () =>
                navigate('/anything-else-toknow', { state: { returning } })
            );
            return;
        }
        const nextRoute = "/whats-next";

        setPageAndDeferNavigate(dispatch, nextRoute, () =>
            navigate(nextRoute, { state: { returning } })
        );
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/insurance/method', navigate);
    };

    return (
        <Question
            onBack={handleBack}
            title="Great! Enter your insurance member ID below."
            returning={returning}
            input={
                <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    <FormLabel id="details-label">
                        <Typography variant="h6">Insurance Details</Typography>
                    </FormLabel>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Member ID"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textFieldSx, width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }
                                }}
                                autoComplete="off"
                                value={id}
                                onChange={(e) => {
                                    setIdError(e.target.value === "");
                                    setId(e.target.value);
                                }}
                                required
                                error={idError}
                                helperText={idError ? "This field is required." : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {/* TODO: has lots of options - autocomplete? */}
                            <TextField
                                label="Relationship to Policy Owner"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textFieldSx, width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }
                                }}
                                value={relat}
                                onChange={(e) => {
                                    setRelError(e.target.value === "");
                                    setRelat(e.target.value);
                                    if (e.target.value === "Self") {
                                        setOwnerFname("");
                                        setOwnerLname("");
                                        setOwnerGender("");
                                        setOwnerDOB(null);
                                    }
                                }}
                                required
                                select
                                error={relError}
                                helperText={relError ? "This field is required." : ""}
                            >
                                {relationships.relations.map((relation) => (
                                    <MenuItem key={relation} value={relation}>
                                        {relation}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Collapse in={relat && relat !== "Self"} mountOnEnter unmountOnExit>
                        <Box sx={{ width: "100%" }}>
                            <FormLabel id="policy-owner-label">
                                <Typography variant="h6" sx={{ mb: "1rem" }}>
                                    Policy Owner Details
                                </Typography>
                            </FormLabel>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="First Name"
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        autoComplete="off"
                                        value={ownerFname}
                                        onChange={(e) => {
                                            setOwnerFnameError(e.target.value === "");
                                            setOwnerFname(e.target.value);
                                        }}
                                        required
                                        error={ownerFnameError}
                                        helperText={ownerFnameError ? "This field is required." : ""}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Last Name"
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        autoComplete="off"
                                        value={ownerLname}
                                        onChange={(e) => {
                                            setOwnerLnameError(e.target.value === "");
                                            setOwnerLname(e.target.value);
                                        }}
                                        required
                                        error={ownerLnameError}
                                        helperText={ownerLnameError ? "This field is required." : ""}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Gender"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        value={ownerGender}
                                        onChange={(e) => {
                                            setOwnerGenderError(e.target.value === "");
                                            setOwnerGender(e.target.value);
                                        }}
                                        required
                                        select
                                        error={ownerGenderError}
                                        helperText={ownerGenderError ? "This field is required." : ""}
                                    >
                                        {genderOpts.map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="Date of Birth"
                                        id="dob"
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    ...textFieldSx,
                                                    width: '100%',
                                                    mb: '2rem',
                                                    "& .MuiInputLabel-root": {
                                                        backgroundColor: theme.palette.background.paper,
                                                        padding: "0 32px",
                                                        transition: "all 0.2s ease-in-out",
                                                    },
                                                },
                                                InputLabelProps: {
                                                    sx: {
                                                        ...inputLabelSx,
                                                        "& .MuiInputLabel-shrink": {
                                                            backgroundColor: theme.palette.background.paper,
                                                        },
                                                    }
                                                },
                                                helperText: ownerDOBError ? dobErrorMessage[ownerDOBError] : "",
                                                error: !!ownerDOBError
                                            }
                                        }}
                                        onChange={(date) => setOwnerDOB(date)}
                                        value={ownerDOB}
                                        onError={(err) => {
                                            if (ownerDOB) {
                                                setOwnerDOBError(err);
                                            }
                                        }}
                                        disableFuture
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title="Continue"
                        disabled={!isNextEnabled}
                        selected={isNextEnabled}
                    />
                </Box>
            }
        />
    );
}
